import api from '../api/client';

export const PREFS_KEY = 'kmt_notify_prefs';
export const NOTIFY_CATEGORIES = ['problems', 'awards', 'stock', 'jobs', 'schedule'];
export const DEFAULT_PREFS = { problems: true, awards: true, stock: true, jobs: true, schedule: true, quiet: false };

export function getPrefs() {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') }; }
  catch (e) { return { ...DEFAULT_PREFS }; }
}
export function savePrefs(prefs) { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); }

function urlB64ToUint8(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export const pushSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

// Register SW + subscribe to Web Push so notifications arrive even when the app is closed. Throws with a readable reason.
export async function enablePush(prefs) {
  if (!pushSupported()) {
    throw new Error(isIOS() && !isStandalone() ? 'ios-install' : 'unsupported');
  }
  const reg = await navigator.serviceWorker.register('/service-worker.js');
  await navigator.serviceWorker.ready;
  const { key } = await api.getVapidKey();
  if (!key) throw new Error('Server push key missing');
  let sub = await reg.pushManager.getSubscription();
  const keyBytes = urlB64ToUint8(key);
  const current = sub?.options?.applicationServerKey;
  if (sub && current && btoa(String.fromCharCode(...new Uint8Array(current))) !== btoa(String.fromCharCode(...keyBytes))) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
  await api.pushSubscribe({ subscription: sub.toJSON(), prefs });
  return sub;
}

// Re-sync this device's subscription on app start (recovers lost/rotated subscriptions).
export async function ensurePush(enabled) {
  if (!enabled || !pushSupported() || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try { await enablePush(getPrefs()); } catch (e) { /* surfaced via Settings test button */ }
}

export async function disablePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await api.pushUnsubscribe({ endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

export async function syncPushPrefs(prefs) {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) await api.pushPrefs({ endpoint: sub.endpoint, prefs }).catch(() => {});
}
